import 'react-native-reanimated';
import { PlanLimitModal } from '@/components/plan-limit-modal';
import { PlanFeatureModal } from '@/components/plan-feature-modal';
import { SeoHead } from '@/components/seo-head';
import { ToastContainer } from '@/components/toast-container';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { flushAuthPersist } from '@/lib/auth-storage';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useOAuthDeepLink } from '@/hooks/use-oauth-deep-link';
import { useThemeHydration } from '@/hooks/use-theme-hydration';
import * as WebBrowser from 'expo-web-browser';
import { queryClient } from '@/lib/query-client';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useRealtimeConnection } from '@/hooks/use-realtime';
import {
  Poppins_100Thin,
  Poppins_100Thin_Italic,
  Poppins_200ExtraLight,
  Poppins_200ExtraLight_Italic,
  Poppins_300Light,
  Poppins_300Light_Italic,
  Poppins_400Regular,
  Poppins_400Regular_Italic,
  Poppins_500Medium,
  Poppins_500Medium_Italic,
  Poppins_600SemiBold,
  Poppins_600SemiBold_Italic,
  Poppins_700Bold,
  Poppins_700Bold_Italic,
  Poppins_800ExtraBold,
  Poppins_800ExtraBold_Italic,
  Poppins_900Black,
  Poppins_900Black_Italic,
} from '@expo-google-fonts/poppins';
import { useFonts } from '@expo-google-fonts/poppins/useFonts';
import { QueryClientProvider } from '@tanstack/react-query';
import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { FEATURES } from '@/constants/feature-flags';
import { getHomePathForUser, isProviderUser } from '@/utils/user-role';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { planTierOf } from '@/utils/plan-access';
import {
  Slot,
  SplashScreen,
  useNavigationContainerRef,
  usePathname,
  useRouter,
  useSegments,
} from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import * as Sentry from '@sentry/react-native';
import { captureLandingPage } from '@/lib/attribution';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, AppState, Platform, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { KeyboardBar } from '@/components/keyboard/keyboard-bar';
import { CookieConsentBanner } from '@/components/legal/cookie-consent-banner';
import { VercelAnalytics } from '@/components/vercel-analytics';
import { CaptchaHost } from '@/components/captcha-host';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ThemedText } from '@/components/themed-text';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { Ionicons } from '@expo/vector-icons';
import { getPublicEnvConfigError } from '@/lib/app-public-env';
import { initSentry, navigationIntegration } from '@/lib/sentry';
import { resolveMembership } from '@/hooks/use-membership';
import { AnimatedSplash, markAppReady, useAppReady } from '@/components/animated-splash';
import '../global.css';
import '@/lib/icon-interop';
import '@/lib/keyboard-interop';
import { themeColors } from '@/hooks/use-theme-colors';

SplashScreen.preventAutoHideAsync();

initSentry();

WebBrowser.maybeCompleteAuthSession();

function getAuthHomePath(user: Parameters<typeof getHomePathForUser>[0]) {
  return getHomePathForUser(user);
}

function RootLayout() {
  const colorScheme = useColorScheme();
  const themeHasHydrated = useThemeHydration();
  const rootViewRef = useRef<View>(null);
  const navigationRef = useNavigationContainerRef();
  const appReady = useAppReady();
  // No nativo a abertura animada cobre a subida e esconde o splash do sistema
  // no primeiro frame dela; na web não existe.
  const [splashDone, setSplashDone] = useState(Platform.OS === 'web');
  useRealtimeConnection();

  useEffect(() => {
    navigationIntegration.registerNavigationContainer(navigationRef);
  }, [navigationRef]);

  // Primeira página aberta no site: é o que diz de qual página orgânica
  // (`/para/barbearia`, `/@slug`) saiu o cadastro.
  useEffect(() => {
    if (Platform.OS === 'web') captureLandingPage();
  }, []);

  const [fontsLoaded, fontError] = useFonts({
    Poppins_100Thin,
    Poppins_100Thin_Italic,
    Poppins_200ExtraLight,
    Poppins_200ExtraLight_Italic,
    Poppins_300Light,
    Poppins_300Light_Italic,
    Poppins_400Regular,
    Poppins_400Regular_Italic,
    Poppins_500Medium,
    Poppins_500Medium_Italic,
    Poppins_600SemiBold,
    Poppins_600SemiBold_Italic,
    Poppins_700Bold,
    Poppins_700Bold_Italic,
    Poppins_800ExtraBold,
    Poppins_800ExtraBold_Italic,
    Poppins_900Black,
    Poppins_900Black_Italic,
  });

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState !== 'background' && nextState !== 'inactive') return;

      const { user, token, isAuthenticated } = useAuthStore.getState();
      if (!token) return;

      void flushAuthPersist({ user, token, isAuthenticated });
    });

    return () => subscription.remove();
  }, []);

  useEffect(() => {
    const initialToken = useAuthStore.getState().token;
    if (initialToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${initialToken}`;
    }
    const unsubscribe = useAuthStore.subscribe((state) => {
      const token = state.token;
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        delete api.defaults.headers.common['Authorization'];
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if ((fontsLoaded || fontError) && themeHasHydrated) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError, themeHasHydrated]);

  // Na web esse portão não pode existir: o HTML estático é gerado com as
  // fontes ainda carregando, então prender a árvore aqui faz cada página do
  // site ser exportada como um spinner — sem texto para buscadores e IAs
  // lerem. A web só usa tema claro e o Poppins entra por CSS, então renderizar
  // direto não muda nada visualmente.
  const isBooting = Platform.OS !== 'web' && ((!fontsLoaded && !fontError) || !themeHasHydrated);

  // O splash fica como irmão fixo da árvore: trocar a estrutura ao terminar
  // remontaria o app inteiro.
  return (
    <>
      {isBooting ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" className="text-primary" />
        </View>
      ) : (
        <AppTree colorScheme={colorScheme} rootViewRef={rootViewRef} />
      )}
      {splashDone ? null : (
        <AnimatedSplash ready={!isBooting && appReady} onFinish={() => setSplashDone(true)} />
      )}
    </>
  );
}

const DARK = themeColors('dark');

/** Fundo das telas do navegador no escuro (sem isso o Stack pinta branco por trás). */
const NAV_DARK_THEME = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    primary: '#FD5001',
    background: DARK.background,
    card: DARK.card,
    text: DARK.foreground,
    border: DARK.border,
  },
};

function AppTree({
  colorScheme,
  rootViewRef,
}: {
  colorScheme: ReturnType<typeof useColorScheme>;
  rootViewRef: React.RefObject<View | null>;
}) {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <KeyboardProvider>
        <SafeAreaProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider value={colorScheme === 'dark' ? NAV_DARK_THEME : DefaultTheme}>
              <SeoHead />
              <View ref={rootViewRef} className="flex-1 bg-background">
                <InitialLayout />
                <StatusBar style={colorScheme === 'dark' ? 'light' : 'dark'} />
                <ToastContainer />
                <PlanLimitModal />
                <PlanFeatureModal />
                <CookieConsentBanner />
                <CaptchaHost />
                <KeyboardBar />
              </View>
              <VercelAnalytics />
            </ThemeProvider>
          </QueryClientProvider>
        </SafeAreaProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}

function InitialLayout() {
  const { isAuthenticated, user, token, fetchProfile } = useAuthStore();
  const segments = useSegments();
  const pathname = usePathname();
  const router = useRouter();
  const isHydrated = useAuthHydration();
  useOAuthDeepLink();
  const [trialExpiredModalOpen, setTrialExpiredModalOpen] = useState(false);
  const [trialExpiredModalShown, setTrialExpiredModalShown] = useState(false);

  const isProvider = useMemo(() => isProviderUser(user), [user]);
  // Teste que o admin deu e acabou: a conta segue no gratuito, e o aviso
  // explica isso uma vez por sessão. Sem `trialEndsAt` nunca houve teste.
  const trialEnded = planTierOf(user) === 'FREE' && !!user?.providerProfile?.trialEndsAt;

  // Só o `GET /user/me` traz o `membership`; as rotas de login devolvem o
  // usuário sem ele. Revalidar uma vez por token — e não só quando falta o
  // usuário — evita a sessão persistida achar que é solo enquanto a conta já
  // virou organização (sidebar sem Equipe, card de transformar de volta).
  const refreshedForToken = useRef<string | null>(null);
  useEffect(() => {
    if (!isHydrated || !token || refreshedForToken.current === token) return;
    refreshedForToken.current = token;
    void fetchProfile();
  }, [isHydrated, token, fetchProfile]);

  useEffect(() => {
    if (!isHydrated) return;
    if (getPublicEnvConfigError()) return;

    const firstSegment = segments[0];
    const isLanding = segments.length === 0 || pathname === '/';
    const inTabsGroup = firstSegment === '(tabs)';
    const inAuthGroup = firstSegment === '(auth)';
    const inCallbackRoute = firstSegment === 'auth';
    const inSubscription = firstSegment === 'subscription';
    const inOnboarding = firstSegment === 'onboarding';
    const inReturnToApp = pathname === '/return-to-app';

    // Let auth callback handle itself
    if (inCallbackRoute) return;

    // Web: fluxo de retorno ao app após cadastro iniciado pelo nativo
    if (Platform.OS === 'web' && inReturnToApp && isAuthenticated && user) {
      return;
    }

    // Mobile: sessão ativa não deve parar na landing nem em login/cadastro
    if (Platform.OS !== 'web' && isAuthenticated && user && (isLanding || inAuthGroup)) {
      router.replace(getAuthHomePath(user));
      return;
    }

    // Web: usuário autenticado não deve ficar na tela de login/cadastro
    if (isAuthenticated && user && inAuthGroup) {
      router.replace(getAuthHomePath(user));
      return;
    }

    // Protect routes - unauthenticated users can't access protected areas
    if (!isAuthenticated && (inTabsGroup || inSubscription)) {
      router.replace('/(auth)/login');
      return;
    }

    // Virou organização e o plano de equipe não foi confirmado: nenhuma tela
    // abre até pagar ou desistir (a saída fica na própria tela de planos).
    if (
      isAuthenticated &&
      user &&
      inTabsGroup &&
      resolveMembership(user.membership).teamPlanPending
    ) {
      router.replace('/subscription/plans');
      return;
    }

    // Redirect provider to onboarding when not yet completed
    if (
      isAuthenticated &&
      user &&
      isProvider &&
      user.providerProfile?.onboardingCompleted === false &&
      !inOnboarding &&
      !inSubscription
    ) {
      router.replace('/onboarding');
      return;
    }
  }, [isAuthenticated, user, segments, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    if (getPublicEnvConfigError()) return;
    if (!FEATURES.STRIPE_SUBSCRIPTIONS) return;
    if (!isAuthenticated) return;
    if (!user) return;
    if (!isProvider) return;
    if (!trialEnded) return;
    if (trialExpiredModalShown) return;

    setTrialExpiredModalShown(true);
    setTrialExpiredModalOpen(true);
  }, [isHydrated, isAuthenticated, user, isProvider, trialEnded, trialExpiredModalShown]);

  const isRestoringSession =
    Platform.OS !== 'web' && !!token && !user && (segments.length === 0 || pathname === '/');

  // Na web só as áreas logadas esperam a sessão hidratar. As páginas públicas
  // (landing, perfil do prestador, agendamento, termos) precisam sair do
  // export estático já com o conteúdo no HTML.
  const isProtectedArea =
    segments[0] === '(tabs)' || segments[0] === 'subscription' || segments[0] === 'onboarding';
  const waitsForSession = Platform.OS !== 'web' || isProtectedArea;
  const holdsSpinner = (!isHydrated && waitsForSession) || isRestoringSession;

  // A abertura só se abre quando a tela real já está aqui, nunca num spinner.
  useEffect(() => {
    if (!holdsSpinner) markAppReady();
  }, [holdsSpinner]);

  if (holdsSpinner) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" className="text-primary" />
      </View>
    );
  }

  return (
    <>
      <Dialog open={trialExpiredModalOpen} onOpenChange={setTrialExpiredModalOpen}>
        <DialogContent className="w-[90%] max-w-md">
          <View className="items-center pt-2">
            <View className="mb-3 h-14 w-14 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
              <Ionicons name="time-outline" size={28} className="text-primary dark:text-white" />
            </View>
            <DialogHeader className="items-center pr-10">
              <DialogTitle className="text-center text-xl text-foreground">
                Seu teste grátis acabou
              </DialogTitle>
              <DialogDescription className="text-center">
                Sua conta continua no plano gratuito: agenda, clientes e cobranças avulsas seguem
                normais. WhatsApp automático, recebimento automático, perfil público, assistente e
                assinaturas voltam quando você assinar.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="mt-6 w-full flex-row gap-3">
              <Button
                title="Agora não"
                variant="outline"
                className="flex-1"
                onPress={() => setTrialExpiredModalOpen(false)}
              />
              <Button
                title="Ver planos"
                className="flex-1"
                onPress={() => {
                  setTrialExpiredModalOpen(false);
                  void navigateToSubscriptionPlans(router);
                }}
              />
            </DialogFooter>
          </View>
        </DialogContent>
      </Dialog>

      <ErrorBoundary route={pathname}>
        <Slot />
      </ErrorBoundary>
    </>
  );
}

export default Sentry.wrap(RootLayout);
